import React, { useEffect, useState, useMemo } from 'react';
import { Card, Button, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

import DeployCommitTable from '../tabels/DeployCommitTable';

import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonInput from 'components/ArgonInput';

import { deployProducts } from 'services/product';
import { useNotifications } from 'context/NotificationContext'; // Import useNotifications
import { createActivity } from 'services/activity';

const CommitButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.info.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.info.dark,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

function DeployCommit({ userId, org, commitProducts, handleStageChange }) {
  const { addNotification } = useNotifications(); // Use the notification hook
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedVersions, setSelectedVersions] = useState({}); // This state seems unused for version selection in this component's UI
  const [successMessage, setSuccessMessage] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  useEffect(() => {
    setSelectedColumns([
      { name: 'product', align: 'left' },
      { name: 'database_version', align: 'center' }, // This refers to selected_version from previous step
      { name: 'org_version', align: 'center' }, // This refers to product_version from SF
    ]);

    // The selectedVersions state and its initialization logic might be redundant
    // if version selection (patch/publish) is not part of this component's UI.
    // If it was intended for a feature not fully implemented, it can be reviewed.
    if (commitProducts) {
      const initialVersions = {};
      commitProducts.forEach((product) => {
        const productId = product.sfId || product.external_id || product.id;
        // Defaulting to 'patch' here might not be necessary if this component doesn't offer version choice.
        initialVersions[productId] = 'patch';
      });
      setSelectedVersions(initialVersions);
    }
  }, [commitProducts]);

  const filteredCommitProducts = useMemo(() => {
    if (!commitProducts || commitProducts.length === 0) {
      return [];
    }
    if (!searchTerm) {
      return commitProducts;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return commitProducts.filter((product) => {
      const productName = product.name ? product.name.toLowerCase() : '';
      // Assuming 'selected_version' is the version from the database/previous step to display
      const productDbVersion = product.selected_version ? String(product.selected_version).toLowerCase() : '';
      return productName.includes(lowerSearchTerm) || productDbVersion.includes(lowerSearchTerm);
    });
  }, [commitProducts, searchTerm]);

  const tableRows = useMemo(() => {
    if (!Array.isArray(filteredCommitProducts)) {
      return [];
    }

    return filteredCommitProducts.map((product) => {
      const productId = product.sfId || product.external_id || product.id || product.sf_id;

      console.log('[filteredCommitProducts] product', product);

      return {
        product: (
          <ArgonTypography ml={3} variant="caption" color="secondary" fontWeight="bold">
            {product.name || 'Unknown Product'}
          </ArgonTypography>
        ),
        // This should display the version chosen in the "Select" step
        database_version: (
          <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
            {product.database_version || 'N/A'}
          </ArgonTypography>
        ),
        // This is the current version in Salesforce
        org_version: (
          <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
            {product.sf_product.product_version || 'N/A'}
          </ArgonTypography>
        ),
        id: productId,
      };
    });
  }, [filteredCommitProducts]); // Removed selectedVersions as it's not directly used in row rendering here

  // handleVersionChange seems unused if there's no UI element to change versions in this component.
  // const handleVersionChange = (productId, selectedVersion) => {
  //   setSelectedVersions((prevVersions) => ({
  //     ...prevVersions,
  //     [productId]: selectedVersion,
  //   }));
  // };

  useEffect(() => {
    setSelectedRows(tableRows);
  }, [tableRows]);

  const commitChanges = async () => {
    if (!org || !org.org_id) {
      addNotification({
        title: ['Error', 'Deployment Pre-check Failed'],
        details: 'Organization ID is missing.',
        severity: 'error',
      });
      return;
    }
    if (!commitProducts || commitProducts.length === 0) {
      addNotification({
        title: ['Info', 'No Products to Deploy'],
        details: 'No products are staged for deployment.',
        severity: 'info',
      });
      return;
    }

    setLoading(true);
    addNotification({
      title: ['Processing', 'Deployment Initiated'],
      details: `Attempting to deploy ${commitProducts.length} product(s) to ${org.name}.`,
      severity: 'info',
    });

    const operationBatchId = `deploy-${Date.now()}`;
    let deploymentResults = []; // To store the outcome of each deployment

    // --- Step 1: Log "In Progress" for all products ---
    const initialActivities = commitProducts.map((product) => {
      const versionToDeploy = product.product_version || product.database_version || 'N/A';

      return {
        operation_type: 'Deploy',
        product_name: product.name || 'Unknown Product',
        product_version: versionToDeploy,
        org_name: org.name,
        status: 'In Progress',
        operationBatchId: operationBatchId,
        view_details: JSON.stringify({
          product_id: product.id || product.sf_id,
          target_org: org.name,
        }),
      };
    });

    if (initialActivities.length > 0) {
      createActivity(initialActivities).catch((logError) =>
        console.error("Failed to log 'Deployment In Progress' activities:", logError)
      );
    }

    try {
      // --- Step 2: Execute the main deployment operation ---
      // The `deployProducts` service already handles parallel execution.
      const response = await deployProducts(org.org_id, commitProducts);

      // The response.details from the service contains the per-product results
      deploymentResults = response.details || [];

      const successfulDeploys = deploymentResults.filter((r) => r.allStepsSuccess);
      const failedDeploys = deploymentResults.filter((r) => !r.allStepsSuccess);

      if (failedDeploys.length > 0) {
        const failedNames = failedDeploys.map((r) => r.productName).join(', ');
        const errorMessage = `Deployment failed for: ${failedNames}.`;
        setError(errorMessage);
        setOpenSnackbar(true);
        addNotification({
          title: ['Error', `Deployment Partially Failed (${successfulDeploys.length}/${commitProducts.length})`],
          details: errorMessage,
          severity: 'error',
        });
      } else {
        const successMsg = `Successfully deployed ${successfulDeploys.length} product(s)!`;
        setSuccessMessage(successMsg);
        setOpenSnackbar(true);
        addNotification({ title: ['Success', 'Deployment Complete'], details: successMsg, severity: 'success' });
      }
    } catch (err) {
      // This catches critical errors if the entire `deployProducts` call fails
      const errorMessage = err.message || 'A critical error occurred during the deployment process.';
      setError(errorMessage);
      setOpenSnackbar(true);
      addNotification({ title: ['Error', 'Deployment Exception'], details: errorMessage, severity: 'error' });
      // In a critical failure, mark all products as failed for logging purposes
      deploymentResults = commitProducts.map((p) => ({
        productName: p.name,
        allStepsSuccess: false,
        steps: [{ step: 'CriticalFailure', success: false, message: errorMessage }],
      }));
    } finally {
      setLoading(false);

      console.log('deploymentResults', deploymentResults);

      if (deploymentResults.length > 0) {
        const finalActivities = deploymentResults.map((result) => {
          const originalProduct = commitProducts.find((p) => p.name === result.productName);
          const versionDeployed = originalProduct?.product_version || originalProduct?.database_version || 'N/A';

          return {
            operation_type: 'Deploy',
            product_name: result.productName,
            product_version: versionDeployed,
            org_name: org.name,
            status: result.allStepsSuccess ? 'Success' : 'Failed',
            operationBatchId: operationBatchId,
            view_details: JSON.stringify(result), // Log the detailed step-by-step result
          };
        });

        createActivity(finalActivities).catch((logError) =>
          console.error('Failed to log final deployment activities:', logError)
        );
      }
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <ArgonBox py={3}>
      <ArgonBox mb={3} display="flex" justifyContent="flex-end">
        <ArgonInput
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: '20rem' }}
        />
      </ArgonBox>
      <ArgonBox mb={3}>
        {(!commitProducts || commitProducts.length === 0) && !loading && (
          <Card>
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="h6" color="textSecondary">
                No Products to Deploy
              </ArgonTypography>
              <ArgonTypography variant="body2" color="textSecondary">
                Please go back and select products for deployment.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        )}
        {commitProducts && commitProducts.length > 0 && (
          <Card>
            <ArgonBox p={3}>
              {selectedColumns.length > 0 && selectedRows.length > 0 ? (
                <DeployCommitTable columns={selectedColumns} rows={selectedRows} />
              ) : (
                <ArgonBox textAlign="center" py={2}>
                  <ArgonTypography variant="body1" color="textSecondary">
                    {searchTerm ? 'No products match your search.' : 'No products to display.'}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        )}
      </ArgonBox>
      <ArgonBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <BackButton variant="contained" onClick={() => handleStageChange('review')} disabled={loading}>
          Back to Review
        </BackButton>
        <CommitButton
          variant="contained"
          onClick={commitChanges}
          disabled={loading || !commitProducts || commitProducts.length === 0}
        >
          {loading ? 'DEPLOYING...' : 'Deploy Changes'}
        </CommitButton>
      </ArgonBox>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={successMessage ? 'success' : 'error'}
          sx={{ alignItems: 'center', alignContent: 'center' }}
          variant="filled"
        >
          {successMessage || error}
        </Alert>
      </Snackbar>
    </ArgonBox>
  );
}

DeployCommit.propTypes = {
  // selectedProducts: PropTypes.array, // This prop seems unused in this component
  // removedProducts: PropTypes.array, // This prop seems unused in this component
  handleStageChange: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
  commitProducts: PropTypes.array, // Products staged for commit from the review step
};

DeployCommit.defaultProps = {
  // Added defaultProps
  commitProducts: [],
};

export default DeployCommit;
